import { useEffect, useRef, useState } from 'react';
import { ptText } from '../lib/ptTime';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Upload, Trophy, Loader2, RefreshCw, Trash2, Plus,
  CheckCircle2, XCircle, Clock, PlayCircle, AlertTriangle, X, Pencil,
  DollarSign, TrendingUp, TrendingDown, Target, Image as ImageIcon, Video, ClipboardPaste, BookOpen, CalendarDays, Layers,
} from 'lucide-react';
import axios from 'axios';
import { PlatformPicker } from '../components/PlatformBadge';
import { MoneyStrip } from '../components/DailyTracker';
import MoneyHistory from '../components/MoneyHistory';
import ActiveProgress from '../components/ActiveProgress';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DailyCodeBadge from '../components/DailyCodeBadge';
import PinProtection from '../components/PinProtection';
import MobileTracker from '../components/MobileTracker';
import BetModeSwitch from '../components/BetModeSwitch';
import ActiveScoreboard from '../components/ActiveScoreboard';
import { WinnerRecipe } from '../components/WinnerRecipe';
import { savedJobs, rememberJob, forgetJob, forgetAllJobs, postUpload, pollUploadJob, cancelJob, keepAwake } from '../lib/uploadJobs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;

const SPORT_NAV = [
  { to: '/sports-betting/ai-picks', id: 'nav-ai-picks', title: '🏈 NFL Picks', sub: 'Safe tickets, who wins, picks not on my tickets, every player', cls: 'border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20' },
  { to: '/sports-betting/nba-picks', id: 'nav-nba-page', title: '🏀 NBA Picks', sub: "Tonight's safe ticket, who wins, points / rebounds / assists / 3s", cls: 'border-orange-500/50 bg-orange-500/10 hover:bg-orange-500/20' },
  { to: '/sports-betting/college', id: 'nav-college-page', title: '🏈 College Football', sub: 'Saturday blowouts and game picks', cls: 'border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20' },
];
const DOING_NAV = [
  { to: '/sports-betting/review', id: 'review-link', title: '🧠 What went wrong', sub: 'What missed and what the app learned' },
  { to: '/sports-betting/results', id: 'nav-results-page', title: '📊 Results', sub: 'Are the % picks hitting?' },
  { to: '/sports-betting/bankroll', id: 'nav-bankroll-page', title: '💵 Money', sub: 'Spent vs won each day' },
];
const SPORT_LABELS = { nfl: 'NFL', nba: 'NBA', cfb: 'College Football' };
const SPORT_COLORS = {
  nfl: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  nba: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  cfb: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
};

const STATUS_META = {
  won:            { color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', icon: CheckCircle2, label: 'Won' },
  lost:           { color: 'text-red-300',     bg: 'bg-red-500/15',     border: 'border-red-500/40',     icon: XCircle,       label: 'Lost' },
  live:           { color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/40',   icon: PlayCircle,    label: 'Live' },
  pending:        { color: 'text-zinc-300',    bg: 'bg-zinc-500/15',    border: 'border-zinc-500/40',    icon: Clock,         label: 'Not Started' },
  push:           { color: 'text-sky-300',     bg: 'bg-sky-500/15',     border: 'border-sky-500/40',     icon: AlertTriangle, label: 'Push' },
  pending_manual: { color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/40',   icon: AlertTriangle, label: 'Needs Manual Grade' },
};

const fmtMoney = (n) => `$${(Number(n || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusPill({ status, size = 'sm' }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  const padding = size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs';
  return (
    <span className={`${meta.bg} ${meta.color} ${meta.border} border rounded-full inline-flex items-center gap-1 ${padding}`} data-testid={`status-${status}`}>
      <Icon className={size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {meta.label}
    </span>
  );
}

function StatsBanner({ stats }) {
  const pnl = stats?.pnl || 0;
  const roi = stats?.roi_percent || 0;
  const PnlIcon = pnl >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="stats-banner">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">Lifetime P&amp;L</p>
        <p className={`text-xl font-bold flex items-center gap-1.5 ${pnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
          <PnlIcon className="w-4 h-4" />
          {pnl >= 0 ? '+' : ''}{fmtMoney(pnl)}
        </p>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">ROI</p>
        <p className={`text-xl font-bold ${roi >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
          {roi >= 0 ? '+' : ''}{roi.toFixed(1)}%
        </p>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">Win Rate</p>
        <p className="text-xl font-bold text-white">{(stats?.win_rate || 0).toFixed(1)}%</p>
        <p className="text-[10px] text-zinc-500">{stats?.wins || 0}W · {stats?.losses || 0}L · {stats?.pending || 0} open</p>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">Total Staked</p>
        <p className="text-xl font-bold text-white">{fmtMoney(stats?.total_stake)}</p>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">Total Won</p>
        <p className="text-xl font-bold text-emerald-300">{fmtMoney(stats?.total_won)}</p>
      </div>
    </div>
  );
}

function LegCard({ leg, parlayId, onManualGrade }) {
  const sport = leg.league ? SPORT_LABELS[leg.league] || leg.sport : (leg.sport || 'Sport');
  const sportBadge = leg.league ? SPORT_COLORS[leg.league] : 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40';
  const hasLive = (leg.live_score_home !== undefined && leg.live_score_home !== null) ||
                  (leg.live_score_away !== undefined && leg.live_score_away !== null);
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid={`leg-card-${leg.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${sportBadge}`}>{sport}</span>
          {leg.bet_type && <span className="text-[10px] uppercase tracking-wider text-zinc-500">{leg.bet_type.replace('_', ' ')}</span>}
        </div>
        <StatusPill status={leg.status || 'pending'} size="xs" />
      </div>
      <div className="mt-2">
        <p className="text-sm text-white font-medium">{leg.selection || '—'}</p>
        {(leg.away_team || leg.home_team) && (
          <p className="text-xs text-zinc-400 mt-0.5">
            {leg.away_team || '?'} @ {leg.home_team || '?'}
          </p>
        )}
      </div>
      {hasLive && (
        <div className="mt-2 rounded-md bg-zinc-950/60 border border-zinc-800 p-2 flex items-center justify-between gap-2">
          <div className="text-xs text-zinc-300">
            <span className="font-semibold">{leg.away_team}</span>
            <span className="mx-1.5 text-zinc-500">—</span>
            <span className="font-bold text-white">{leg.live_score_away ?? 0}</span>
          </div>
          <div className="text-[10px] text-amber-300 uppercase tracking-wider">{ptText(leg.live_period)}</div>
          <div className="text-xs text-zinc-300">
            <span className="font-bold text-white">{leg.live_score_home ?? 0}</span>
            <span className="mx-1.5 text-zinc-500">—</span>
            <span className="font-semibold">{leg.home_team}</span>
          </div>
        </div>
      )}
      {leg.odds && (
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="text-zinc-500">Odds</span>
          <span className="text-white font-mono">{leg.odds}</span>
        </div>
      )}
      {/* 2026-02-XX (user request) — Live player-prop stat row.  Shows
          current numeric stat (e.g. "Mahomes 139 pass yds") when ESPN
          returns a value, so the user never has to grade manually. */}
      {(leg.bet_type === 'player_prop' && leg.live_stat_value !== undefined && leg.live_stat_value !== null) && (
        <div className="mt-2 rounded-md bg-amber-500/5 border border-amber-500/30 p-2 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">
            Live stat
          </span>
          <span className="text-sm text-white font-bold font-mono">
            {leg.live_stat_value}{leg.line ? ` / ${leg.line}` : ''}
          </span>
        </div>
      )}
      {leg.result_note && (
        <p className="text-[10px] text-zinc-500 mt-1 italic">{leg.result_note}</p>
      )}
      {/* 2026-02-XX (user request) — Manual grade buttons only show on
          non-player-prop legs now that player props grade live.  Player
          props flagged pending_manual (ESPN missing data) still get the
          fallback. */}
      {(leg.status === 'pending_manual') && leg.status !== 'won' && leg.status !== 'lost' && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] text-zinc-500">Manual grade:</span>
          <button className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25" onClick={() => onManualGrade(leg.id, 'won')} data-testid={`manual-won-${leg.id}`}>Won</button>
          <button className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25" onClick={() => onManualGrade(leg.id, 'lost')} data-testid={`manual-lost-${leg.id}`}>Lost</button>
          <button className="text-[10px] px-2 py-0.5 rounded bg-sky-500/15 border border-sky-500/40 text-sky-300 hover:bg-sky-500/25" onClick={() => onManualGrade(leg.id, 'push')} data-testid={`manual-push-${leg.id}`}>Push</button>
        </div>
      )}
    </div>
  );
}

const DupBadge = ({ d, testId, small = false }) => (d ? (
  <span className={`inline-block rounded bg-red-600 font-black text-white ${small ? 'px-1.5 text-[10px]' : 'mb-2 w-full px-2 py-1 text-xs'}`} data-testid={testId}>
    DUPLICATE — already uploaded{d.created_at ? ` ${new Date(d.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}{d.archived ? ' (in Settled)' : ''}
  </span>
) : null);

const isVideo = (f) => (f.type || '').startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(f.name || '');

// Paste box: Ctrl/Cmd+V (or long-press → Paste) screenshots OR ticket text.
function PasteTicketsBox({ onImages, onText, busy, reading }) {
  const [text, setText] = useState('');
  const onPaste = (e) => {
    const imgs = Array.from(e.clipboardData?.items || [])
      .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
      .map((it) => it.getAsFile()).filter(Boolean);
    if (imgs.length) { e.preventDefault(); onImages(imgs); }
  };
  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900/60 p-4 space-y-2" data-testid="paste-box">
      <p className="text-sm text-white font-semibold flex items-center gap-2"><ClipboardPaste className="w-4 h-4 text-amber-300" /> Paste your tickets</p>
      <p className="text-xs text-zinc-500">Paste screenshots (they upload instantly) or copy the ticket text from your sportsbook and paste it here — several tickets at once is fine.</p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onPaste={onPaste}
        rows={5}
        disabled={busy}
        placeholder="Tap here and paste…"
        className="w-full rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white p-3 focus:outline-none focus:border-amber-500"
        data-testid="paste-textarea"
      />
      <Button size="sm" disabled={busy || text.trim().length < 10} onClick={() => onText(text)}
        className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="paste-read-btn">
        {reading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Read pasted tickets
      </Button>
    </div>
  );
}

function UploadDialog({ open, onClose, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [pasting, setPasting] = useState(false);
  const [draft, setDraft] = useState(null);        // single-parlay editing view
  const [draftList, setDraftList] = useState([]);  // multi-parlay view (video with multiple tickets)
  const [joinPick, setJoinPick] = useState([]);  // drafts ticked to combine into one ticket

  // Glue split pieces of one long ticket back together (up to 40 legs), in the order shown.
  const combineDrafts = () => {
    const idxs = joinPick.length >= 2 ? [...joinPick].sort((a, b) => a - b) : draftList.map((_, i) => i);
    const parts = idxs.map((i) => draftList[i]);
    const seen = new Set();
    const legs = parts.flatMap((p) => p.legs || []).filter((l) => {
      if (!l.selection && !l.away_team) return false;
      const k = `${String(l.selection || '').trim().toLowerCase().replace(/\s+/g, ' ')}|${l.away_team || ''}@${l.home_team || ''}`.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    if (legs.length > 40) { toast.error(`That would be ${legs.length} legs — one ticket holds up to 40`); return; }
    const pick = (k) => parts.map((p) => p[k]).find((v) => v);
    const stakes = new Set(parts.map((p) => Number(p.stake) || 0).filter(Boolean));
    if (stakes.size > 1) toast.warning(`Those pieces had different bet amounts — kept $${pick('stake')}; fix it in the editor if needed`, { duration: 8000 });
    const merged = { ...parts[0], stake: pick('stake'), potential_payout: pick('potential_payout'), total_odds: pick('total_odds'), sportsbook: pick('sportsbook'), legs, leg_count: legs.length };
    const rest = draftList.filter((_, i) => !idxs.includes(i));
    rest.splice(idxs[0], 0, merged);
    setJoinPick([]);
    checkDupes(rest);
    if (rest.length === 1) { setDraft(rest[0]); setDraftList([]); } else setDraftList(rest);
    toast.success(`Combined into one ${legs.length}-leg ticket`);
  };
  const [editingLegIdx, setEditingLegIdx] = useState(null);
  const [platform, setPlatform] = useState(null);
  const inputRef = useRef(null);
  const ctrlRef = useRef(null);
  const busyRef = useRef(false);
  const jobsRef = useRef(new Set());
  const platformRef = useRef(null);
  platformRef.current = platform;

  useEffect(() => {
    if (!open && !busyRef.current) { setDraft(null); setDraftList([]); setEditingLegIdx(null); setPlatform(null); setDupes({}); }
    const left = savedJobs();
    if (open && !busyRef.current && left.length) {  // pick up an upload that was still reading when the page was closed/reloaded
      setPlatform(left[0].platform || 'fanduel');
      platformRef.current = left[0].platform || 'fanduel';
      runAll(left.map((j) => ({ jobId: j.id, label: j.label || 'upload' })), 'your earlier upload');
    }
  }, [open]);

  const handleFiles = async (fileList) => {
    const chosen = Array.from(fileList || []).filter(Boolean);
    if (chosen.length === 0) return;
    if (!platform) { toast.error('Pick Kalshi or FanDuel first'); return; }
    const isVid = isVideo;
    const tooBig = chosen.filter((f) => isVid(f) && f.size > 60 * 1024 * 1024);
    if (tooBig.length) toast.error(`${tooBig.map((f) => f.name).join(', ')} over 60 MB — trim the recording and re-upload`);
    const vids = chosen.filter((f) => isVid(f) && f.size <= 60 * 1024 * 1024);
    const imgs = chosen.filter((f) => !isVid(f));
    // Each video is its own background job (read side by side); all screenshots go in one job.
    const batches = [...(imgs.length ? [{ files: imgs, label: `${imgs.length} screenshot${imgs.length === 1 ? '' : 's'}` }] : []),
      ...vids.map((f, i) => ({ files: [f], label: `video ${i + 1} (${f.name})` }))];
    if (!batches.length) return;
    await runAll(batches, `${chosen.length} file${chosen.length === 1 ? '' : 's'}`);
  };

  // Runs every batch (3 at a time). Keeps going while you're in another app; Stop kills it everywhere.
  const runAll = async (batches, from) => {
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    busyRef.current = true;
    setUploading(true);
    const lock = await keepAwake();
    let done = 0;
    setUploadProgress(batches.length > 1 ? `0 of ${batches.length} done` : '');
    const runBatch = async (b) => {
      let id = b.jobId;
      try {
        let data;
        if (!id) {
          const fd = new FormData();
          b.files.forEach((f) => fd.append(isVideo(f) ? 'file' : 'files', f));
          data = await postUpload(fd, ctrl.signal);
          id = data?.job_id;
        }
        if (id) {
          jobsRef.current.add(id);
          rememberJob({ id, label: b.label, platform: platformRef.current });
          data = await pollUploadJob(id, ctrl.signal);
        }
        const parlays = Array.isArray(data.parlays) ? data.parlays : (data.legs ? [data] : []);
        return { parlays, errors: data.errors || [] };
      } catch (e) {
        return { parlays: [], errors: e.stopped ? [] : [`${b.label}: ${e.response?.data?.detail || e.message}`] };
      } finally {
        if (id) { forgetJob(id); jobsRef.current.delete(id); }
        done += 1;
        if (batches.length > 1 && !ctrl.signal.aborted) setUploadProgress(`${done} of ${batches.length} done`);
      }
    };
    const results = [];
    const queue = [...batches];
    await Promise.all(Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (queue.length && !ctrl.signal.aborted) results.push(await runBatch(queue.shift()));
    }));
    lock?.release?.().catch(() => {});
    busyRef.current = false;
    if (ctrl.signal.aborted) return;
    const seen = new Set();
    const parlays = results.flatMap((r) => r.parlays).filter((p) => {
      const key = `${(p.legs || []).map((l) => l.selection).sort().join('|')}#${p.stake || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const errors = results.flatMap((r) => r.errors);
    setUploading(false);
    setUploadProgress('');
    if (!parlays.length) { toast.error(`Extraction failed: ${errors.join('; ') || 'no tickets found'}`, { duration: 15000 }); return; }
    showResult({ parlays, errors }, from);
  };

  const stopLoading = () => {
    ctrlRef.current?.abort();
    jobsRef.current.forEach((id) => cancelJob(id));
    jobsRef.current.clear();
    forgetAllJobs();
    busyRef.current = false;
    setUploading(false);
    setUploadProgress('');
    toast.info('Stopped — nothing from that upload was saved');
  };

  const handleText = async (text) => {
    if (!platform) { toast.error('Pick Kalshi or FanDuel first'); return; }
    setUploading(true);
    setPasting(true);
    try {
      const res = await axios.post(`${API}/parse-text`, { text });
      showResult(res.data || {}, 'pasted text');
    } catch (e) {
      toast.error(`Couldn't read the text: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
      setPasting(false);
    }
  };

  // Warn right away when an uploaded ticket is already on the board.
  const [dupes, setDupes] = useState({});
  const checkDupes = async (list) => {
    setDupes({});
    try {
      const { data } = await axios.post(`${API}/parlays/check-duplicates`, { parlays: list });
      const map = Object.fromEntries((data.duplicates || []).map((d) => [d.index, d]));
      setDupes(map);
      const n = Object.keys(map).length;
      if (n) toast.warning(n === list.length && n === 1 ? 'Duplicate — this ticket is already uploaded' : `${n} of these tickets ${n === 1 ? 'is' : 'are'} already uploaded (marked DUPLICATE)`, { duration: 5000, position: 'bottom-center' });
    } catch { /* the check is a helper — saving still works without it */ }
  };

  const showResult = (data, from) => {
      if (data.errors?.length) toast.warning(`${data.errors.length} screenshot${data.errors.length > 1 ? 's' : ''} skipped — re-upload: ${data.errors.join('; ')}`, { duration: 15000 });
      const list = Array.isArray(data.parlays) && data.parlays.length ? data.parlays : [data];
      checkDupes(list);
      if (list.length > 1) {
        setDraftList(data.parlays);
        toast.success(`Found ${data.parlays.length} parlays from ${from}`);
      } else {
        const only = Array.isArray(data.parlays) ? data.parlays[0] : data;
        setDraft(only);
        toast.success(`Extracted ${only.legs?.length || 0} legs`);
      }
  };

  // Legacy single-file handler retained for drag-and-drop callers
  const handleFile = async (file) => handleFiles(file ? [file] : []);

  const handleSave = async () => {
    try {
      const res = await axios.post(`${API}/parlays`, { ...draft, platform });
      if (res.data?.archived_reason === 'previously_deleted') toast.info('You deleted this ticket before, so it went to Settled');
      else if (res.data?.archived_reason === 'already_settled') toast.info('This ticket is already in Settled — it was not added again');
      else if (res.data?.duplicate_of) toast.warning('Duplicate — this ticket was already on your board, so it was updated instead of added twice', { duration: 8000 });
      else toast.success('Parlay saved');
      onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const handleSaveAll = async () => {
    try {
      let n = 0, skipped = 0, dup = 0;
      for (const p of draftList) {
        const r = await axios.post(`${API}/parlays`, { ...p, platform });
        if (['previously_deleted', 'already_settled'].includes(r.data?.archived_reason)) skipped++;
        else if (r.data?.duplicate_of) dup++;
        else n++;
      }
      const msg = `Saved ${n} new ticket${n === 1 ? '' : 's'}${dup ? ` · ${dup} DUPLICATE${dup === 1 ? '' : 'S'} already on your board (not added twice)` : ''}${skipped ? ` · ${skipped} already deleted/settled, sent to Settled` : ''}`;
      if (dup) toast.warning(msg, { duration: 10000 }); else toast.success(msg);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const removeDraftFromList = (idx) => {
    setDraftList((lst) => lst.filter((_, i) => i !== idx));
    setJoinPick([]);
    setDupes((m) => Object.fromEntries(Object.entries(m).filter(([k]) => +k !== idx).map(([k, v]) => [+k > idx ? +k - 1 : +k, v])));
  };

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const updateLeg = (idx, patch) => setDraft((d) => {
    const legs = [...(d.legs || [])];
    legs[idx] = { ...legs[idx], ...patch };
    return { ...d, legs };
  });
  const removeLeg = (idx) => setDraft((d) => ({ ...d, legs: (d.legs || []).filter((_, i) => i !== idx) }));
  const addLeg = () => setDraft((d) => ({ ...d, legs: [...(d.legs || []), { id: Math.random().toString(36).slice(2, 10), status: 'pending', league: 'nfl', bet_type: 'spread' }] }));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-3xl my-6" data-testid="upload-dialog">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Upload className="w-4 h-4 text-amber-300" />
            Upload parlay ticket
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="close-upload"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!draft && !draftList.length && <PlatformPicker value={platform} onChange={setPlatform} />}
          {!draft && !draftList.length && (
            <div
              className={`rounded-xl border-2 border-dashed border-zinc-700 p-10 text-center cursor-pointer hover:border-amber-500/50 hover:bg-amber-500/5 transition-colors ${platform ? '' : 'opacity-40 pointer-events-none'}`}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer?.files); }}
              data-testid="dropzone"
            >
              <input ref={inputRef} type="file" multiple className="hidden" accept="image/*,video/*" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="ticket-file-input" />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-amber-300 animate-spin" />
                  <p className="text-sm text-white">Reading your tickets with AI…</p>
                  {uploadProgress && <p className="text-xs font-bold text-amber-300" data-testid="upload-progress">{uploadProgress}</p>}
                  <p className="text-xs text-zinc-500">Screenshots ~5 seconds each · each video about a minute, read 3 at a time</p>
                  <p className="text-[11px] text-emerald-300/90" data-testid="upload-keeps-going">Keeps reading if you switch apps or windows — come back any time.</p>
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); stopLoading(); }}
                    className="pointer-events-auto mt-1 border-red-500/60 bg-transparent text-red-300 hover:bg-red-500/10" data-testid="stop-loading-btn">
                    <X className="w-4 h-4 mr-1" /> Stop loading
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="flex gap-2">
                    <ImageIcon className="w-6 h-6 text-zinc-400" />
                    <Video className="w-6 h-6 text-zinc-400" />
                  </div>
                  <p className="text-sm text-white">Drop {platform === 'kalshi' ? 'Kalshi' : platform === 'fanduel' ? 'FanDuel' : 'ticket'} screenshots or a screen recording here</p>
                  <p className="text-xs text-zinc-500">PNG · JPG · MP4 · MOV &nbsp;·&nbsp; up to 20 MB per image / 60 MB video</p>
                  <p className="text-[10px] text-amber-300/80 mt-1">Multiple parlays? Select several screenshots and/or several screen recordings at once — I'll put each ticket in its own bracket.</p>
                  <Button size="sm" className="mt-2 bg-amber-500 hover:bg-amber-600 text-black" data-testid="choose-file-btn">Choose file(s)</Button>
                </div>
              )}
            </div>
          )}
          {!draft && !draftList.length && platform && (
            <PasteTicketsBox busy={uploading} reading={pasting} onImages={handleFiles} onText={handleText} />
          )}

          {draftList.length > 0 && !draft && (
            <div className="space-y-3" data-testid="multi-draft-list">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm text-white">
                  <span className="text-amber-300 font-bold">{draftList.length}</span> parlays detected — review and save
                  <span className="block text-[11px] text-zinc-400">One long ticket got split? Tick its pieces (or none for all) and combine.</span>
                </p>
                <Button size="sm" variant="outline" onClick={combineDrafts} className="border-amber-500/60 bg-transparent text-amber-200 hover:bg-amber-500/10" data-testid="combine-drafts-btn">
                  <Layers className="w-4 h-4 mr-1" /> {joinPick.length >= 2 ? `Combine ${joinPick.length} into one ticket` : 'Combine all into one ticket'}
                </Button>
                <Button size="sm" onClick={handleSaveAll} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-all-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Save all {draftList.length}
                </Button>
              </div>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {draftList.map((p, idx) => (
                  <div key={p.id || idx} className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3" data-testid={`draft-item-${idx}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <input type="checkbox" checked={joinPick.includes(idx)} aria-label="Pick to combine" className="accent-amber-400"
                          onChange={() => setJoinPick((j) => (j.includes(idx) ? j.filter((x) => x !== idx) : [...j, idx]))} data-testid={`combine-pick-${idx}`} />
                        <span className="text-xs text-amber-300 font-mono">Parlay {idx + 1}</span>
                        <DupBadge d={dupes[idx]} testId={`duplicate-badge-${idx}`} small />
                        <span className="text-xs text-zinc-400">{p.sportsbook || 'FanDuel'}</span>
                        <span className="text-xs text-white font-semibold">{(p.legs || []).length}-leg</span>
                        <span className="text-xs text-zinc-500 font-mono">{p.total_odds}</span>
                        <span className="text-xs text-zinc-400">${(Number(p.stake) || 0).toFixed(2)} → <span className="text-emerald-300">${(Number(p.potential_payout) || 0).toFixed(2)}</span></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button className="text-xs text-amber-300 hover:text-amber-200" onClick={() => { setDraft(p); setDraftList([]); }} data-testid={`edit-draft-${idx}`}>
                          <Pencil className="w-3 h-3 inline mr-0.5" /> Edit
                        </button>
                        <button className="text-xs text-red-400 hover:text-red-300" onClick={() => removeDraftFromList(idx)} data-testid={`remove-draft-${idx}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1.5 space-y-0.5">
                      {(p.legs || []).slice(0, 5).map((l, i) => (
                        <div key={i} className="text-[11px] text-zinc-400 flex items-center gap-1.5">
                          <span className="text-zinc-600">•</span>
                          <span>{l.selection || `${l.away_team} @ ${l.home_team}`}</span>
                          {l.odds && <span className="text-zinc-500 font-mono ml-auto">{l.odds}</span>}
                        </div>
                      ))}
                      {(p.legs || []).length > 5 && <p className="text-[10px] text-zinc-600 italic">+ {p.legs.length - 5} more legs</p>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                <button onClick={() => { setDraftList([]); }} className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="redo-upload-multi">← Upload different files</button>
              </div>
            </div>
          )}

          {draft && (
            <>
              <DupBadge d={dupes[0]} testId="duplicate-badge" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Sportsbook</label>
                  <Input value={draft.sportsbook || ''} onChange={(e) => updateDraft({ sportsbook: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white h-9" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Stake $</label>
                  <Input type="number" step="0.01" value={draft.stake ?? ''} onChange={(e) => updateDraft({ stake: parseFloat(e.target.value) || 0 })} className="bg-zinc-900 border-zinc-700 text-white h-9" data-testid="draft-stake" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Total odds</label>
                  <Input value={draft.total_odds || ''} onChange={(e) => updateDraft({ total_odds: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white h-9" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Payout $</label>
                  <Input type="number" step="0.01" value={draft.potential_payout ?? ''} onChange={(e) => updateDraft({ potential_payout: parseFloat(e.target.value) || 0 })} className="bg-zinc-900 border-zinc-700 text-white h-9" data-testid="draft-payout" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm text-white font-semibold">Legs ({draft.legs?.length || 0})</h4>
                  <button onClick={addLeg} className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1" data-testid="add-leg-btn"><Plus className="w-3 h-3" /> Add leg</button>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {(draft.legs || []).map((leg, idx) => (
                    <div key={leg.id || idx} className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-2">
                      <div className="flex items-center gap-2">
                        <select value={leg.league || ''} onChange={(e) => updateLeg(idx, { league: e.target.value })} className="bg-zinc-950 border border-zinc-700 rounded text-xs h-7 px-1 text-white" data-testid={`leg-${idx}-league`}>
                          <option value="nfl">NFL</option>
                          <option value="nba">NBA</option>
                          <option value="cfb">CFB</option>
                        </select>
                        <select value={leg.bet_type || 'spread'} onChange={(e) => updateLeg(idx, { bet_type: e.target.value })} className="bg-zinc-950 border border-zinc-700 rounded text-xs h-7 px-1 text-white" data-testid={`leg-${idx}-type`}>
                          <option value="spread">Spread</option>
                          <option value="total">Total</option>
                          <option value="moneyline">Moneyline</option>
                          <option value="player_prop">Player Prop</option>
                        </select>
                        <Input value={leg.odds || ''} onChange={(e) => updateLeg(idx, { odds: e.target.value })} placeholder="Odds" className="h-7 bg-zinc-950 border-zinc-700 text-white text-xs w-20" data-testid={`leg-${idx}-odds`} />
                        <button onClick={() => removeLeg(idx)} className="ml-auto text-red-400 hover:text-red-300" data-testid={`leg-${idx}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                        <Input value={leg.away_team || ''} onChange={(e) => updateLeg(idx, { away_team: e.target.value })} placeholder="Away team" className="h-7 bg-zinc-950 border-zinc-700 text-white text-xs" data-testid={`leg-${idx}-away`} />
                        <Input value={leg.home_team || ''} onChange={(e) => updateLeg(idx, { home_team: e.target.value })} placeholder="Home team" className="h-7 bg-zinc-950 border-zinc-700 text-white text-xs" data-testid={`leg-${idx}-home`} />
                      </div>
                      <Input value={leg.selection || ''} onChange={(e) => updateLeg(idx, { selection: e.target.value })} placeholder="Selection (e.g. Cowboys -3.5, OVER 48.5, Mahomes OVER 275.5 passing yards)" className="h-7 bg-zinc-950 border-zinc-700 text-white text-xs mt-1.5" data-testid={`leg-${idx}-selection`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
                <button onClick={() => setDraft(null)} className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="redo-upload">← Upload different ticket</button>
                <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-parlay-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Save parlay
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ParlayDetail({ parlay, onBack, onRefresh, onDelete, onManualGrade }) {
  const [refreshing, setRefreshing] = useState(false);
  const legCountWon = (parlay.legs || []).filter((l) => l.status === 'won').length;
  const legCountTotal = (parlay.legs || []).length;
  const handleRefresh = async () => {
    setRefreshing(true);
    try { await onRefresh(parlay.id); toast.success('Scores refreshed'); } catch (e) { toast.error(e.message); } finally { setRefreshing(false); }
  };
  return (
    <div className="space-y-4" data-testid="parlay-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onBack} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="back-to-list"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleRefresh} disabled={refreshing} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="refresh-parlay-btn">
            {refreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh scores
          </Button>
          <Button size="sm" variant="outline" onClick={() => { if (window.confirm('Delete this parlay?')) onDelete(parlay.id); }} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="delete-parlay-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-950/40 to-zinc-900 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">{parlay.sportsbook || 'Parlay'}</p>
            <h3 className="text-xl font-bold text-white">
              {legCountTotal}-Leg Parlay · <span className="font-mono">{parlay.total_odds || ''}</span>
            </h3>
          </div>
          <StatusPill status={parlay.status || 'pending'} />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div><p className="text-[10px] uppercase text-zinc-500">Stake</p><p className="text-lg font-semibold text-white">{fmtMoney(parlay.stake)}</p></div>
          <div><p className="text-[10px] uppercase text-zinc-500">Potential payout</p><p className="text-lg font-semibold text-emerald-300">{fmtMoney(parlay.potential_payout)}</p></div>
          <div><p className="text-[10px] uppercase text-zinc-500">Legs hit</p><p className="text-lg font-semibold text-white">{legCountWon} / {legCountTotal}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(parlay.legs || []).map((leg) => (
          <LegCard key={leg.id} leg={leg} parlayId={parlay.id} onManualGrade={(legId, status) => onManualGrade(parlay.id, legId, status)} />
        ))}
      </div>
    </div>
  );
}

function ParlayList({ parlays, onOpen, onRefreshAll, refreshing }) {
  return (
    <div className="space-y-2" data-testid="parlay-list">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">All Parlays ({parlays.length})</h3>
        <Button size="sm" onClick={onRefreshAll} disabled={refreshing} className="bg-zinc-800 hover:bg-zinc-700 text-white" data-testid="refresh-all-btn">
          {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Refresh all live
        </Button>
      </div>
      {parlays.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-800 p-10 text-center">
          <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">No parlays yet. Tap <span className="text-amber-300 font-semibold">Upload Ticket</span> to add your first one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {parlays.map((p) => {
            const legsHit = (p.legs || []).filter((l) => l.status === 'won').length;
            return (
              <button key={p.id} onClick={() => onOpen(p.id)} className="w-full text-left rounded-xl border border-zinc-800 hover:border-amber-500/40 bg-zinc-900/60 hover:bg-zinc-900 p-3 transition-colors" data-testid={`parlay-row-${p.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-zinc-500 font-mono">{(p.created_at || '').slice(0, 10)}</span>
                    <span className="text-sm text-white font-semibold">{(p.legs || []).length}-leg</span>
                    <span className="text-xs text-zinc-400">{p.sportsbook || 'Parlay'}</span>
                    <span className="text-xs text-zinc-500 font-mono">{p.total_odds || ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-400">{fmtMoney(p.stake)} → <span className="text-emerald-300 font-semibold">{fmtMoney(p.potential_payout)}</span></span>
                    <span className="text-xs text-zinc-500">{legsHit}/{(p.legs || []).length}</span>
                    <StatusPill status={p.status || 'pending'} size="xs" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SportsBettingPage() {
  const location = useLocation();
  // Opened from the dashboard (already signed in) → no sports PIN; links opened directly still ask for it.
  const [authed, setAuthed] = useState(() => {
    if (location.state?.fromDashboard) {
      sessionStorage.setItem('sports_betting_authed', 'true');
      window.history.replaceState({}, '');
    }
    return sessionStorage.getItem('sports_betting_authed') === 'true';
  });
  const [activeTab, setActiveTab] = useState('tracker');  // 'tracker' | 'parlays' | 'ai_picks'
  const [parlays, setParlays] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeParlay, setActiveParlay] = useState(null);
  const [trackerKey, setTrackerKey] = useState(0);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parlays`);
      setParlays(res.data.parlays || []);
      setStats(res.data.stats || {});
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/parlays/${id}`);
      setActiveParlay(res.data);
      setActiveId(id);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { if (authed) loadList(); }, [authed]);
  useEffect(() => { if (authed && savedJobs().length) setUploadOpen(true); }, [authed]);

  const handleRefreshOne = async (id) => {
    const res = await axios.post(`${API}/parlays/${id}/refresh`);
    setActiveParlay(res.data);
    await loadList();
  };
  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/refresh-all`);
      toast.success(`Refreshed ${res.data.refreshed} parlay${res.data.refreshed === 1 ? '' : 's'}`);
      await loadList();
      if (activeId) await loadDetail(activeId);
    } catch (e) { toast.error(e.message); } finally { setRefreshing(false); }
  };
  const handleDelete = async (id) => {
    await axios.delete(`${API}/parlays/${id}`);
    toast.success('Parlay deleted');
    setActiveId(null); setActiveParlay(null);
    await loadList();
  };
  const handleManualGrade = async (parlayId, legId, status) => {
    try {
      const res = await axios.post(`${API}/parlays/${parlayId}/legs/${legId}/manual-grade`, { status });
      setActiveParlay(res.data);
      await loadList();
      toast.success(`Leg marked ${status}`);
    } catch (e) { toast.error(e.message); }
  };

  if (!authed) {
    return (
      <PinProtection
        onAuthenticated={() => { sessionStorage.setItem('sports_betting_authed', 'true'); setAuthed(true); }}
        label="Sports Betting Tracker"
      />
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-6" data-testid="sports-betting-page">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/" className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="exit-sports-betting"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            <h1 className="text-lg md:text-xl font-bold">Sports Betting Tracker</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <DailyCodeBadge />
            <Link to="/sports-betting/ledger" className="inline-flex h-9 items-center gap-1 rounded-md border border-zinc-700 px-3 text-sm font-semibold text-zinc-200 hover:bg-zinc-800" data-testid="open-ledger-btn">
              <BookOpen className="w-4 h-4" /> Ledger
            </Link>
            <Link to="/sports-betting/schedule" className="inline-flex h-9 items-center gap-1 rounded-md border border-zinc-700 px-3 text-sm font-semibold text-zinc-200 hover:bg-zinc-800" data-testid="open-schedule-btn">
              <CalendarDays className="w-4 h-4" /> Schedule
            </Link>
            <Button onClick={() => setUploadOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="open-upload-btn">
              <Upload className="w-4 h-4 mr-1" /> Upload Ticket
            </Button>
          </div>
        </div>

        <ActiveProgress refreshKey={trackerKey} />
        <WinnerRecipe />
        <MoneyHistory refreshKey={trackerKey} />
        <MoneyStrip refreshKey={trackerKey} />

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 space-y-1" data-testid="bet-mode-card">
          <p className="text-xs font-bold text-zinc-300">How should picks be explained?</p>
          <BetModeSwitch />
        </div>

        <section className="space-y-2" data-testid="picks-nav">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Picks — pick a sport</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {SPORT_NAV.map((x) => (
              <Link key={x.to} to={x.to} data-testid={x.id}
                className={`flex items-center justify-between gap-3 rounded-xl border p-3 transition-colors ${x.cls}`}>
                <div>
                  <p className="text-sm font-bold text-white">{x.title}</p>
                  <p className="text-[11px] text-zinc-400">{x.sub}</p>
                </div>
                <span className="shrink-0 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-bold text-white">Open</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="space-y-2" data-testid="howdoing-nav">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">How am I doing?</p>
          <div className="grid grid-cols-3 gap-2">
            {DOING_NAV.map((x) => (
              <Link key={x.to} to={x.to} data-testid={x.id}
                className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-2.5 hover:border-emerald-500/60 transition-colors">
                <p className="text-xs font-bold text-white">{x.title}</p>
                <p className="text-[10px] text-zinc-500 leading-snug">{x.sub}</p>
              </Link>
            ))}
          </div>
        </section>

        {activeTab === 'tracker' ? (
          <div className="space-y-4">
          <ActiveScoreboard reloadKey={trackerKey} />
          <MobileTracker reloadKey={trackerKey} />
          </div>


        ) : (
          <>
            <StatsBanner stats={stats} />
            {activeParlay ? (
              <ParlayDetail
                parlay={activeParlay}
                onBack={() => { setActiveParlay(null); setActiveId(null); }}
                onRefresh={handleRefreshOne}
                onDelete={handleDelete}
                onManualGrade={handleManualGrade}
              />
            ) : (
              <ParlayList
                parlays={parlays}
                onOpen={loadDetail}
                onRefreshAll={handleRefreshAll}
                refreshing={refreshing}
              />
            )}
          </>
        )}

        <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onSaved={() => { loadList(); setActiveTab('tracker'); setTrackerKey((k) => k + 1); }} />
      </div>
    </div>
  );
}
